import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Filter, Eye, EyeOff, Layers } from 'lucide-react';
import './ChartFilters.css';

const PLANET_CATEGORIES = {
  regular: {
    label: 'Regular Planets',
    planets: ['mercury', 'venus', 'earth', 'mars', 'jupiter', 'saturn', 'uranus', 'neptune'],
  },
  dwarf: {
    label: 'Dwarf Planets',
    planets: ['pluto', 'chiron', 'pholus', 'ceres', 'pallas', 'juno', 'vesta'],
  },
  horizons: {
    label: 'Horizons Dwarf Planets',
    planets: ['makemake', 'eris', 'quaoar', 'sedna', 'haumea', 'ixion', 'gonggong', 'orcus'],
  },
};

const getPlanetCategory = (planetName) => {
  const name = planetName?.toLowerCase() || '';
  if (PLANET_CATEGORIES.regular.planets.includes(name)) {
    return 'regular';
  }
  if (PLANET_CATEGORIES.dwarf.planets.includes(name)) {
    return 'dwarf';
  }
  if (PLANET_CATEGORIES.horizons.planets.includes(name)) {
    return 'horizons';
  }
  // Default any unknown/higher-octave bodies to the dwarf category so they stay visible
  return 'dwarf';
};

const ASPECT_STRENGTHS = [
  { id: 'very_strong', label: 'Very Strong (0-1°)', default: true },
  { id: 'strong', label: 'Strong (1-3°)', default: true },
  { id: 'moderate', label: 'Moderate (3-6°)', default: true },
];

const ASPECT_TYPES = [
  { id: 'conjunction', label: 'Conjunction', default: true },
  { id: 'opposition', label: 'Opposition', default: true },
  { id: 'square', label: 'Square', default: true },
  { id: 'trine', label: 'Trine', default: true },
  { id: 'sextile', label: 'Sextile', default: true },
  { id: 'minor', label: 'Minor Aspects', default: true },
];

const ChartFilters = ({ 
  planets, 
  aspects, 
  onPlanetsChange, 
  onAspectsChange,
  onDisplayModeChange,
  displayMode 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Initialize filters based on planet categories - Show All Planets by default
  const [planetFilters, setPlanetFilters] = useState(() => ({
    regular: true,
    dwarf: true,
    horizons: true,
  }));

  // Initialize aspect strength filters
  const [aspectStrengthFilters, setAspectStrengthFilters] = useState(
    ASPECT_STRENGTHS.reduce((acc, strength) => {
      acc[strength.id] = strength.default;
      return acc;
    }, {})
  );

  // Initialize aspect type filters
  const [aspectTypeFilters, setAspectTypeFilters] = useState(
    ASPECT_TYPES.reduce((acc, type) => {
      acc[type.id] = type.default;
      return acc;
    }, {})
  );

  // Filter planets based on category filters
  const filteredPlanets = useMemo(
    () =>
      planets.filter((planet) => {
        const category = getPlanetCategory(planet.name);
        if (category in planetFilters) {
          return planetFilters[category];
        }
        return true;
      }),
    [planets, planetFilters],
  );

  // Filter aspects based on strength and type filters
  const filteredAspects = useMemo(() => {
    return aspects.filter((aspect) => {
      // Check strength filter
      const strengthId = aspect.strength?.id || 'moderate';
      if (!aspectStrengthFilters[strengthId]) {
        return false;
      }

      // Check type filter
      const aspectType = aspect.aspect?.toLowerCase();
      const isMinor = aspect.type === 'minor';
      
      if (isMinor) {
        return aspectTypeFilters.minor;
      }
      
      return aspectTypeFilters[aspectType] !== false;
    });
  }, [aspects, aspectStrengthFilters, aspectTypeFilters]);

  // Update planet filters
  const handlePlanetCategoryToggle = (category) => {
    setPlanetFilters(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  // Update aspect strength filters
  const handleAspectStrengthToggle = (strengthId) => {
    setAspectStrengthFilters(prev => ({
      ...prev,
      [strengthId]: !prev[strengthId],
    }));
  };

  // Update aspect type filters
  const handleAspectTypeToggle = (typeId) => {
    setAspectTypeFilters(prev => ({
      ...prev,
      [typeId]: !prev[typeId],
    }));
  };

  // Notify parent of changes when filters change
  useEffect(() => {
    onPlanetsChange(filteredPlanets);
  }, [filteredPlanets, onPlanetsChange]);

  useEffect(() => {
    onAspectsChange(filteredAspects);
  }, [filteredAspects, onAspectsChange]);

  return (
    <div className="chart-filters">
      <button
        className="chart-filters__toggle"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-label="Toggle chart filters"
      >
        <Filter size={18} />
        <span>Chart Filters</span>
        <span className="chart-filters__toggle-icon">{isExpanded ? '−' : '+'}</span>
      </button>

      {isExpanded && (
        <div className="chart-filters__content">
          {/* Display Mode Selection */}
          <div className="chart-filters__section">
            <h5 className="chart-filters__section-title">
              <Layers size={16} />
              Display Mode
            </h5>
            <div className="chart-filters__radio-group">
              <label className="chart-filters__radio">
                <input
                  type="radio"
                  name="displayMode"
                  value="all"
                  checked={displayMode === 'all'}
                  onChange={(e) => onDisplayModeChange(e.target.value)}
                />
                <span>All Planets (Single Circle)</span>
              </label>
              <label className="chart-filters__radio">
                <input
                  type="radio"
                  name="displayMode"
                  value="rings"
                  checked={displayMode === 'rings'}
                  onChange={(e) => onDisplayModeChange(e.target.value)}
                />
                <span>Inner/Outer Rings</span>
              </label>
            </div>
          </div>

          {/* Planet Category Filters */}
          <div className="chart-filters__section">
            <h5 className="chart-filters__section-title">
              <Eye size={16} />
              Planet Categories
            </h5>
            <div className="chart-filters__checkbox-group">
              {Object.entries(PLANET_CATEGORIES).map(([key, category]) => (
                <label key={key} className="chart-filters__checkbox">
                  <input
                    type="checkbox"
                    checked={planetFilters[key]}
                    onChange={() => handlePlanetCategoryToggle(key)}
                  />
                  <span className="chart-filters__checkbox-icon">
                    {planetFilters[key] ? <Eye size={14} /> : <EyeOff size={14} />}
                  </span>
                  <span>{category.label}</span>
                  <span className="chart-filters__count">
                    ({planets.filter(p => getPlanetCategory(p.name) === key).length})
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Aspect Strength Filters */}
          <div className="chart-filters__section">
            <h5 className="chart-filters__section-title">Aspect Strength</h5>
            <div className="chart-filters__checkbox-group">
              {ASPECT_STRENGTHS.map((strength) => (
                <label key={strength.id} className="chart-filters__checkbox">
                  <input
                    type="checkbox"
                    checked={aspectStrengthFilters[strength.id]}
                    onChange={() => handleAspectStrengthToggle(strength.id)}
                  />
                  <span className={`chart-filters__strength-indicator chart-filters__strength-indicator--${strength.id}`} />
                  <span>{strength.label}</span>
                  <span className="chart-filters__count">
                    ({aspects.filter(a => (a.strength?.id || 'moderate') === strength.id).length})
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Aspect Type Filters */}
          <div className="chart-filters__section">
            <h5 className="chart-filters__section-title">Aspect Types</h5>
            <div className="chart-filters__checkbox-group">
              {ASPECT_TYPES.map((type) => (
                <label key={type.id} className="chart-filters__checkbox">
                  <input
                    type="checkbox"
                    checked={aspectTypeFilters[type.id]}
                    onChange={() => handleAspectTypeToggle(type.id)}
                  />
                  <span className={`chart-filters__aspect-indicator chart-filters__aspect-indicator--${type.id}`} />
                  <span>{type.label}</span>
                  <span className="chart-filters__count">
                    ({aspects.filter(a => {
                      if (type.id === 'minor') return a.type === 'minor';
                      return a.aspect?.toLowerCase() === type.id;
                    }).length})
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="chart-filters__section">
            <div className="chart-filters__actions">
              <button
                className="chart-filters__action-btn"
                onClick={() => {
                  setPlanetFilters({
                    regular: true,
                    dwarf: true,
                    horizons: true,
                  });
                }}
              >
                Show All Planets
              </button>
              <button
                className="chart-filters__action-btn"
                onClick={() => {
                  setAspectStrengthFilters(
                    ASPECT_STRENGTHS.reduce((acc, s) => {
                      acc[s.id] = true;
                      return acc;
                    }, {})
                  );
                }}
              >
                Show All Strengths
              </button>
              <button
                className="chart-filters__action-btn chart-filters__action-btn--secondary"
                onClick={() => {
                  setAspectStrengthFilters(
                    ASPECT_STRENGTHS.reduce((acc, s) => {
                      acc[s.id] = s.id === 'very_strong' || s.id === 'strong';
                      return acc;
                    }, {})
                  );
                }}
              >
                Strong Only
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

ChartFilters.propTypes = {
  planets: PropTypes.array.isRequired,
  aspects: PropTypes.array.isRequired,
  onPlanetsChange: PropTypes.func.isRequired,
  onAspectsChange: PropTypes.func.isRequired,
  onDisplayModeChange: PropTypes.func.isRequired,
  displayMode: PropTypes.oneOf(['all', 'rings']).isRequired,
};

export default ChartFilters;

